import { useAppDispatch } from '@user/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from '@sales-center/components'
import {
  getUserRecordsAssignedSalesTeamList,
  IGroupedSalesTeam,
  ISalesTeamUser,
} from '@user/store/users'
import { getUserListForSalesTeamsByTeam } from '@user/store/users/hooks/useGetUserListForSalesTeamsByTeam'
import { selectAllSalesTeams } from '@user/store/salesTeam'
import { useSelector } from 'react-redux'
import { useCheckAdminPermissions } from '@user/store/authorization/permissions/useCheckPermissions'
import { SettingsAdminPermOps } from '@user/store/authorization'

export interface IUserReassignOpenRecords {
  userId: string
  reassignLeads: boolean
  reassignOpps: boolean
}

const UNASSIGNED_OPTION: ISalesTeamUser = {
  id: 'unassigned',
  fullName: 'Unassigned',
}

export function useData(props: IUserReassignOpenRecords) {
  const { userId, reassignLeads, reassignOpps } = props
  const dispatch = useAppDispatch()
  const allSalesTeams = useSelector(selectAllSalesTeams)
  const [salesTeams, setGroupedSalesTeams] = useState<IGroupedSalesTeam[]>([])
  const [usersByTeamId, setUsersByTeamId] = useState<
    Record<number, ISalesTeamUser[]>
  >({})
  const [loadingTeamIds, setLoadingTeamIds] = useState<Record<number, boolean>>(
    {},
  )
  const requestedTeamsRef = useRef<Set<number>>(new Set())
  const [showMore, setShowMore] = useState<boolean>(false)
  const [allUnassigned, setAllUnassigned] = useState<boolean>(false)
  const [formValid, setFormValid] = useState<boolean>(false)

  const [status, setStatus] = useState<'pending' | 'success' | 'fail'>(
    'pending',
  )

  const [showAdminUsers] = useCheckAdminPermissions(SettingsAdminPermOps)

  const fetchTeamsUsers = useCallback(
    async (teamIds: number[]) => {
      const newTeamIds = Array.from(new Set(teamIds)).filter(
        (id) => !requestedTeamsRef.current.has(id),
      )
      if (newTeamIds.length === 0) return

      newTeamIds.forEach((id) => requestedTeamsRef.current.add(id))
      setLoadingTeamIds((prev) => {
        const next = { ...prev }
        newTeamIds.forEach((id) => (next[id] = true))
        return next
      })

      try {
        const options = (await getUserListForSalesTeamsByTeam(newTeamIds)) || []
        const adminOptions = options.filter((option) => option.isAdmin)

        const usersForTeam = (teamId: number): ISalesTeamUser[] => {
          const seen = new Set<string>()
          const members = options.filter(
            (option) =>
              !option.isAdmin && Number(option.salesTeamId) === teamId,
          )
          return [
            UNASSIGNED_OPTION,
            ...[...adminOptions, ...members]
              .filter(
                (option) =>
                  option.isActive &&
                  option.userId !== userId &&
                  (showAdminUsers ? true : !option.isAdmin),
              )
              .filter((option) => {
                if (seen.has(option.userId)) return false
                seen.add(option.userId)
                return true
              })
              .map((option) => ({
                id: option.userId,
                fullName: option.name || 'Unknown',
              }))
              .sort((a, b) => a.fullName.localeCompare(b.fullName)),
          ]
        }

        setUsersByTeamId((prev) => {
          const next = { ...prev }
          newTeamIds.forEach((id) => (next[id] = usersForTeam(id)))
          return next
        })
      } catch {
        newTeamIds.forEach((id) => requestedTeamsRef.current.delete(id))
        toast.error('Failed to load users for the selected sales team')
      } finally {
        setLoadingTeamIds((prev) => {
          const next = { ...prev }
          newTeamIds.forEach((id) => (next[id] = false))
          return next
        })
      }
    },
    [userId, showAdminUsers],
  )

  useEffect(() => {
    const execute = async () => {
      try {
        const result = await dispatch(
          getUserRecordsAssignedSalesTeamList({
            userId,
            reassignLeads,
            reassignOpps,
          }),
        )
        if (getUserRecordsAssignedSalesTeamList.fulfilled.match(result)) {
          setStatus('success')

          const filteredSalesTeams = result.payload.map((record) => ({
            ...record,
            salesTeam: allSalesTeams.find(
              (team) => team.id === record.salesTeamId,
            ),
          }))

          const groupedSalesTeams = Object.values(
            filteredSalesTeams.reduce<Record<string, IGroupedSalesTeam>>(
              (acc, record) => {
                const branchId = record.salesTeam?.branchId || 'Unassigned'

                if (!acc[branchId]) {
                  acc[branchId] = {
                    branchName: record.salesTeam?.branchName || 'Unassigned',
                    filteredSalesTeams: [],
                    allSalesTeams: allSalesTeams
                      .filter((team) => team.branchId === branchId)
                      .sort((a, b) => a.name.localeCompare(b.name)),
                  }
                }

                if (record.salesTeam) {
                  acc[branchId].filteredSalesTeams.push({
                    leadIds: record.leadIds,
                    opportunityIds: record.opportunityIds,
                    openRecords: record.openRecords,
                    ...record.salesTeam,
                  })
                }
                return acc
              },
              {},
            ),
          )

          setGroupedSalesTeams(groupedSalesTeams)

          const defaultTeamIds = groupedSalesTeams.flatMap((branch) =>
            branch.filteredSalesTeams.map((team) => team.id),
          )
          fetchTeamsUsers(defaultTeamIds)
        } else {
          setStatus('fail')
        }
      } catch (error) {
        setStatus('fail')
        toast.error(error.Message)
      }
    }
    execute()
  }, [
    allSalesTeams,
    dispatch,
    fetchTeamsUsers,
    userId,
    reassignLeads,
    reassignOpps,
  ])

  const onChangeShowMore = useCallback(
    () => setShowMore(!showMore),
    [setShowMore, showMore],
  )

  return {
    groupedSalesTeams: salesTeams,
    status,
    setGroupedSalesTeams,
    usersByTeamId,
    fetchTeamsUsers,
    loadingTeamIds,
    showMore,
    setShowMore,
    allUnassigned,
    setAllUnassigned,
    formValid,
    setFormValid,
    onChangeShowMore,
  }
}
